import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useSocket } from 'sonata-platform-components';
import { showNotification } from 'sonata-react-components';

type FileReadyData = { urlFile: string; entityId: string; fileFormat: string };
type FileErrorData = { entityId: string; fileFormat: string; message: string };

type Options = {
  entityId: string;
  onFileError: () => void;
  onFileReady: (urlFile: string, fileFormat: string) => void;
};

export function useFootfallReportSocket({ entityId, onFileError, onFileReady }: Readonly<Options>) {
  const { formatMessage } = useIntl();
  const { offEvent, onEvent } = useSocket();

  const fileReadyKey = `footfallReportSocketFileReady_${entityId}`;
  const fileErrorKey = `footfallReportSocketFileError_${entityId}`;

  useEffect(() => {
    onEvent(fileReadyKey, 'footfallReportSocketFileReady', (data: FileReadyData) => {
      if (data.entityId !== entityId) {
        return;
      }

      onFileReady(data.urlFile, data.fileFormat);

      showNotification(
        'success',
        formatMessage({
          defaultMessage: 'File generated successfully',
          id: 'driveToStore.aggregatedReport.storeDetail.fileReady'
        })
      );
    });

    onEvent(fileErrorKey, 'footfallReportSocketFileError', (data: FileErrorData) => {
      if (data.entityId !== entityId) {
        return;
      }

      onFileError();

      showNotification(
        'error',
        formatMessage({
          defaultMessage: 'Error generating file. Please try again.',
          id: 'driveToStore.aggregatedReport.storeDetail.fileError'
        })
      );
    });

    return () => {
      offEvent(fileReadyKey);
      offEvent(fileErrorKey);
    };
  }, [entityId]);
}
